import { useEffect, useMemo, useState } from 'react';
import {
  CartLineCategoryT,
  CartLineT,
  LineItemType,
} from '@lambda/apis/src/cart/types';
import { ShippingSKU } from '@lambda/apis/src/shipping/types';
import { ProductCategoryWarranty } from '@lambda/apis/src/category/types';
import { CartWarrantyT } from '@/lib/reebelocare/types';
import { getWarrantyInfoByCategoryPks } from '@/lib/warranty';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';

type UseAddonsResult = {
  warranties: CartWarrantyT;
  expressShippingOffers: Record<string, ShippingSKU>;
  /** Standard (free) warranty per device category, keyed by category pk. */
  categoryWarranties: Record<string, ProductCategoryWarranty>;
};

/**
 * Fetches the per-line add-ons shown in the cart — ReebeloCare warranties and
 * express shipping offers — and returns them keyed by cart line id.
 */
export default function useAddons(): UseAddonsResult {
  const { cartLines } = useCartContext();

  const [warranties, setWarranties] = useState<CartWarrantyT>({});
  const [expressShippingOffers, setExpressShippingOffers] = useState<
    Record<string, ShippingSKU>
  >({});
  const [categoryWarranties, setCategoryWarranties] = useState<
    Record<string, ProductCategoryWarranty>
  >({});

  const cartPskus = useMemo(
    () =>
      cartLines
        .filter((line) => line.type === LineItemType.PRODUCT)
        .map((line) => line.psku)
        .filter((p): p is string => Boolean(p)),
    [cartLines],
  );

  // ReebeloCare warranties (eligibility + pricing per product)
  useEffect(() => {
    if (cartPskus.length === 0) {
      setWarranties({});

      return undefined;
    }

    const controller = new AbortController();

    const fetchWarranties = async () => {
      let eligibilityMap: Record<string, boolean>;

      try {
        eligibilityMap = await fetch(
          `/api/cart/warranty-eligiblity?pskus=${cartPskus.join('|')}`,
          { signal: controller.signal },
        ).then((res) => res.json());
      } catch {
        // Request was aborted (cart changed/unmounted) or failed — ignore.
        return;
      }

      // A plan is priced by the device's own price band, so it's looked up per
      // line: the same SKU from two sellers at different prices can need two
      // different plans. Lines sharing a price and category share a lookup.
      const priceGroups = cartLines
        .filter(
          (line) =>
            line.type === LineItemType.PRODUCT &&
            line.psku &&
            eligibilityMap[line.psku],
        )
        .reduce<Record<string, CartLineT[]>>((acc, line) => {
          const key = `${line.price}|${line.category?.pk ?? ''}`;

          if (!acc[key]) {
            acc[key] = [];
          }

          acc[key].push(line);

          return acc;
        }, {});

      const result: CartWarrantyT = {};

      await Promise.all(
        Object.values(priceGroups).map(async (lines) => {
          const firstLine = lines[0];

          if (!firstLine.category?.pk) {
            return;
          }

          const productWarranty = await fetch(
            `/api/warranty/search-warranty-sku?price=${
              firstLine.price / 100
            }&categoryPk=${encodeURIComponent(firstLine.category.pk)}`,
            { signal: controller.signal },
          )
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null);

          if (!productWarranty) {
            return;
          }

          for (const line of lines) {
            result[line.id] = { productWarranty };
          }
        }),
      );

      setWarranties(result);
    };

    fetchWarranties();

    return () => controller.abort();
  }, [cartPskus]);

  // Standard warranty per device category, which the ReebeloCare choice on a
  // line item compares the plan against.
  const categoryPksKey = useMemo(
    () =>
      Array.from(
        new Set(
          cartLines
            .filter((line) => line.type === LineItemType.PRODUCT)
            .map((line) => line.category?.pk)
            .filter((pk): pk is string => Boolean(pk)),
        ),
      )
        .sort()
        .join('|'),
    [cartLines],
  );

  useEffect(() => {
    const categoryPks = categoryPksKey ? categoryPksKey.split('|') : [];

    if (categoryPks.length === 0) {
      setCategoryWarranties({});

      return undefined;
    }

    let isCurrent = true;

    // The endpoint answers in the order it was asked.
    getWarrantyInfoByCategoryPks(categoryPks).then((infos) => {
      if (!isCurrent || !infos) {
        return;
      }

      setCategoryWarranties(
        Object.fromEntries(
          categoryPks.map((pk, index) => [pk, infos[index]] as const),
        ),
      );
    });

    return () => {
      isCurrent = false;
    };
  }, [categoryPksKey]);

  // Express shipping offers (one per vendor + category, mapped to each line)
  useEffect(() => {
    const shippableLines = cartLines.filter(
      (line): line is CartLineT & { category: CartLineCategoryT } =>
        Boolean(
          line.vendor &&
            line.category?.pk &&
            line.type === LineItemType.PRODUCT,
        ),
    );

    if (shippableLines.length === 0) {
      setExpressShippingOffers({});

      return undefined;
    }

    const controller = new AbortController();

    const fetchExpressShipping = async () => {
      const groups = shippableLines.reduce<
        Record<string, typeof shippableLines>
      >((acc, line) => {
        const key = `${line.vendor}|${line.category.pk}`;

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(line);

        return acc;
      }, {});

      const result: Record<string, ShippingSKU> = {};

      await Promise.all(
        Object.values(groups).map(async (lines) => {
          const firstLine = lines[0];
          const params = new URLSearchParams({
            vendor: firstLine.vendor.split('#')[1],
            categoryPk: firstLine.category.pk,
          });

          const shippingOffer: ShippingSKU | null = await fetch(
            `/api/express-shipping/offer?${params}`,
            { signal: controller.signal },
          )
            .then((res) => (res.ok ? res.json() : null))
            .then((json) => json?.shippingOffer ?? null)
            .catch(() => null);

          if (!shippingOffer) {
            return;
          }

          for (const line of lines) {
            result[line.id] = shippingOffer;
          }
        }),
      );

      setExpressShippingOffers(result);
    };

    fetchExpressShipping();

    return () => controller.abort();
  }, [cartLines]);

  return { warranties, expressShippingOffers, categoryWarranties };
}
